import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { StepWrapper } from '@/common/components/ConnectionSteps/StepWrapper'

type RemovalTypeStepProps = {
  appData: {
    applicationAccountUserId: string
    applicationName: string
    accountName: string
  }
  stepInfo: {
    current: number
    total: number
  }
  onTypeChange?: (type: 'partial' | 'total' | null) => void
  currentType?: 'partial' | 'total' | null
}

export function RemovalTypeStep({
  appData,
  stepInfo,
  onTypeChange,
  currentType,
}: RemovalTypeStepProps) {
  const { t } = useTranslation(['common', 'profile'])
  const [selectedType, setSelectedType] = useState<'partial' | 'total' | null>(
    currentType || null,
  )

  useEffect(() => {
    setSelectedType(currentType || null)
  }, [currentType])

  const handleTypeChange = (type: 'partial' | 'total') => {
    setSelectedType(type)
    if (onTypeChange) {
      onTypeChange(type)
    }
  }

  const getDisplayName = () => {
    if (appData.accountName && appData.accountName.trim()) {
      return `${appData.applicationName} (${appData.accountName})`
    }
    return appData.applicationName
  }

  return (
    <StepWrapper
      appData={{
        applicationId: '',
        applicationName: appData.applicationName,
        userId: '',
      }}
      stepInfo={stepInfo}
      accountName={appData.accountName}
    >
      <div className="space-y-6">
        <div className="text-sm text-gray-600">
          {t('profile:access.modal.remove_access.type_selection.description', {
            accountName: appData.accountName,
          })}
        </div>

        <div className="space-y-4">
          <div
            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
              selectedType === 'partial'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleTypeChange('partial')}
          >
            <div className="flex items-start space-x-3">
              <input
                type="radio"
                id="partial"
                name="removalType"
                checked={selectedType === 'partial'}
                onChange={() => handleTypeChange('partial')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">
                  {t(
                    'profile:access.modal.remove_access.type_selection.partial.title',
                  )}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t(
                    'profile:access.modal.remove_access.type_selection.partial.description',
                    {
                      displayName: getDisplayName(),
                    },
                  )}
                </p>
              </div>
            </div>
          </div>

          <div
            className={`border rounded-lg p-4 cursor-pointer transition-colors ${
              selectedType === 'total'
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
            onClick={() => handleTypeChange('total')}
          >
            <div className="flex items-start space-x-3">
              <input
                type="radio"
                id="total"
                name="removalType"
                checked={selectedType === 'total'}
                onChange={() => handleTypeChange('total')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">
                  {t(
                    'profile:access.modal.remove_access.type_selection.total.title',
                  )}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t(
                    'profile:access.modal.remove_access.type_selection.total.description',
                    {
                      displayName: getDisplayName(),
                    },
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </StepWrapper>
  )
}
